import { store } from "../redux/store";
import FormData from "form-data";
import api from "./client";

import {
  setAllBusiness,
  setBusinessDashboardData,
  setBusinessProfile,
  setIsNextGymSelected,
  setMembershipUpgraded,
  setPartnerGymInfo,
  setPartnerGyms,
  setUserCanDowngradeToBasic,
  setUserData,
  setUserMembershipStatus,
} from "../slices/appSlice";
import {
  createBusinessProfile,
  getAllBusiness,
  getBusinessDashboardData,
  getBusinessProfile,
  getPartnerGymInfo,
  getPartnerGyms,
  getStripeConfig,
  getUserMembershipStatus,
  refreshUser,
  subscribeToNextGym,
  updateGallery,
  uploadImage,
  updateMembershipOnboarding,
  updateMembershipConversion,
  validateDiscountCode,
  recordDiscountCodeUsage,
  sendWelcomeEmail,
  cancelSubscriptionImmediately,
  attachPaymentMethodToSubscription,
  deleteMemberSettings,
  fetchBusinessOwnerEmailByName,
  openBillingPortal as openBillingPortalApi,
  getUserReferralCode,
  submitTampaWaitlistEntry,
  submitOrlandoWaitlistEntry,
  submitNewYorkWaitlistEntry,
  submitPartnerLead,
  startPhoneVerification,
  checkPhoneVerification,
  checkDuplicates,
  clearNextProduct,
  setRenewalTier,
  undoNextGym,
  previewGymSwitch,
  switchCurrentGym,
} from "./apiEndpoints";
import { daysUntilExpiration } from "../utils/utils";
import { YOU_CANT_REPEAT_GYM } from "../utils/strings";
import { getAuth } from "firebase/auth";

const dispatch = store.dispatch;

// ------- User  -------

export const getUserCall = async () => {
  try {
    const result = await refreshUser();
    dispatch(setUserData(result?.data.user));
  } catch (error) {
    // console.log("getUserCall()", error);
  }
};

export const getUserMembershipStatusCall = async () => {
  try {
    const result = await getUserMembershipStatus();
    dispatch(setUserMembershipStatus(result?.data));
    dispatch(
      setMembershipUpgraded(
        result.data?.settings?.nextProduct?.name === "premium"
      )
    );
    // This is the case when a user has bought a basic membership and has just upgrade for the upcoming month, we want to let the user downgrade to basic only during the month when the upgraded membership has started
    dispatch(
      setUserCanDowngradeToBasic(
        result?.data?.memberships?.active?.product?.name === "premium" &&
          result?.data?.settings?.nextProduct?.name === "premium"
      )
    );
    dispatch(
      setIsNextGymSelected(
        result.data?.settings?.nextGymChoiceMode === "pick" ||
          result.data?.settings?.nextGymChoiceMode === null
      )
    );
  } catch (error) {
    // console.log("getUserMembershipStatusCall()", error);
  }
};

export const sendWelcomeEmailCall = async (
  email: string,
  firstName: string,
  gymName: string,
  gymEmail: string,
  userPhone: string,
  startDate: string,
  fullName: string 
) => {
  try {
    const result = await sendWelcomeEmail(
      email,
      firstName,
      gymName,
      gymEmail,
      userPhone,
      startDate,
      fullName 
    );
    // console.log("Emails sent successfully:", result);
    return "success";
  } catch (error) {
    // console.log("sendWelcomeEmailCall error:", error);
    return "error";
  }
};

export const startPhoneVerificationCall = async (phoneNumber: string) => {
  try {
    const result = await startPhoneVerification(phoneNumber);
    // console.log("📤 Verification code sent:", result);
    return { success: true };
  } catch (error) {
    // console.error("startPhoneVerificationCall error:", error);
    return { success: false, message: "Failed to send verification code." };
  }
};

export const checkPhoneVerificationCall = async (phoneNumber: string, code: string) => {
  try {
    const result = await checkPhoneVerification(phoneNumber, code);
    // console.log("✅ Phone verified:", result);
    return { success: true };
  } catch (error) {
    // console.error("checkPhoneVerificationCall error:", error);
    return { success: false, message: "Verification failed." };
  }
};

export const checkDuplicatesCall = async (phone: string, email?: string) => {
  try {
    const result = await checkDuplicates(phone, email);
    return {
      success: result.data.success,
      isPhoneUnique: result.data.isPhoneUnique,
      isEmailUnique: result.data.isEmailUnique,
      phoneMessage: result.data.phoneMessage,
      emailMessage: result.data.emailMessage,
    };
  } catch (error: any) {
    const data = error?.response?.data || {};
    return {
      success: false,
      isPhoneUnique: data.isPhoneUnique,
      isEmailUnique: data.isEmailUnique,
      phoneMessage: data.phoneMessage,
      emailMessage: data.emailMessage,
      message: data.message || "Something went wrong.",
    };
  }
};

// ------- Payments  -------

export const getStripeConfigCall = async () => {
  try {
    const result = await getStripeConfig();
    const { publishableKey } = result?.data;
    return publishableKey;
  } catch (error) {
    // console.log("getStripeConfigCall", error);
  }
};

export const cancelSubscriptionImmediatelyCall = async (subscriptionId: string) => {
  try {
    const result = await cancelSubscriptionImmediately({ subscriptionId });
    return result?.data;
  } catch (error) {
    // console.error("cancelSubscriptionImmediatelyCall error:", error);
    return { success: false };
  }
};


export const attachPaymentMethodToSubscriptionCall = async (
  subscriptionId: string,
  paymentMethodId: string
) => {
  try {
    const auth = getAuth();
    const user = auth.currentUser;
    const idToken = await user?.getIdToken();

    if (!idToken) throw new Error("Authorization token missing");

    const result = await attachPaymentMethodToSubscription(
      subscriptionId,
      paymentMethodId,
      idToken
    );

    // console.log("✅ Attached payment method to subscription:", result?.data);
    return { success: true };
  } catch (error) {
    // console.error("🚨 attachPaymentMethodToSubscriptionCall error:", error);
    return { success: false };
  }
};

export const deleteMemberSettingsCall = async (userId: string) => {
  try {
    const result = await deleteMemberSettings(userId);
    return result?.data;
  } catch (error) {
    // console.error("deleteMemberSettingsCall error:", error);
    return { success: false };
  }
};

export const clearNextProductCall = async () => {
  try {
    const res = await clearNextProduct();
    return res?.data;
  } catch (error) {
    return { success: false };
  }
};

export const setRenewalTierCall = async (nextProductId: string) => {
  try {
    const res = await setRenewalTier(nextProductId);
    return res?.data;
  } catch (error) {
    return { success: false };
  }
};

export const undoNextGymCall = async () => {
  try {
    const res = await undoNextGym();
    return res?.data;
  } catch (error) {
    return { success: false };
  }
};

export const previewGymSwitchCall = async (newBusinessProductId: string) => {
  try {
    const res = await previewGymSwitch(newBusinessProductId);
    return { success: true, data: res?.data };
  } catch (error: any) {
    return { success: false, message: 'Unable to switch gyms at this time. Please try again.' };
  }
};

export const switchCurrentGymCall = async (newBusinessProductId: string): Promise<boolean> => {
  try {
    const res = await switchCurrentGym(newBusinessProductId);
    await getUserMembershipStatusCall();
    return res?.status === 200;
  } catch (error) {
    return false;
  }
};


export const openBillingPortalCall = async (): Promise<string | null> => {
  try {
    const result = await openBillingPortalApi();
    if (result?.url) {
      return result.url;
    } else {
      // console.error("No billing portal URL returned");
      return null;
    }
  } catch (error) {
    // console.error("openBillingPortalCall error:", error);
    return null;
  }
};

// ------- Bussines (Gym Owners)  -------

export const getPartnerGymInfoCall = async () => {
  try {
    const result = await getPartnerGymInfo();
    const partnerGymInfo = result?.data?.business;
    dispatch(setPartnerGymInfo(partnerGymInfo));
  } catch (error) {
    // console.log("getPartnerGymInfoCall", error);
  }
};

export const getPartnerGymsCall = async () => {
  try {
    const result = await getPartnerGyms();
    const businesses = result?.data?.businesses ?? [];
    dispatch(setPartnerGyms(businesses));
    if (businesses.length > 0) {
      dispatch(setPartnerGymInfo(businesses[0]));
    }
  } catch (error) {
    // console.log("getPartnerGymsCall", error);
  }
};

export const getBusinessProfileCall = async (businessId: string) => {
  try {
    const result = await getBusinessProfile(businessId);
    const businessData = result?.data?.business;
    dispatch(setBusinessProfile(businessData));
  } catch (error) {
    // console.log("getBusinessProfileCall", error);
  }
};

export const getBusinessDashboardDataCall = async (businessId: string) => {
  try {
    const result = await getBusinessDashboardData(businessId);
    dispatch(setBusinessDashboardData(result?.data));
  } catch (error) {
    // console.log("getBusinessDashboardDataCall", error);
  }
};

export const getAllBusinessCall = async () => {
  try {
    const result = await getAllBusiness();
    const businessData = result?.data?.business;
    dispatch(setAllBusiness(businessData));
  } catch (error) {
    // console.log("getAllBusinessCall", error);
  }
};

export const subscribeToNextGymCall = async (businessProductId: string) => {
  try {
    const result = await subscribeToNextGym(businessProductId);
    const success = result?.status === 200;
    dispatch(setIsNextGymSelected(success));
    await getUserMembershipStatusCall();
    return result?.status;
  } catch (error: any) {
    // console.log("subscribeToNextGymCall", error);
    if (error?.response?.data?.errors?.[0] === YOU_CANT_REPEAT_GYM) {
      return 2;
    } else {
      return 1;
    }
  }
};
export const createBusinessProfileCall = async (data: any) => {
  try {
    const result = await createBusinessProfile(data);
    const profile = result?.data;
    dispatch(setBusinessProfile(profile));
    if (result?.status === 200 || result?.status === 201) {
      await getPartnerGymInfoCall();
      return "success";
    }
  } catch (error) {
    alert(`Create partner profile status:, ${error}`);
    return "error";
  }
};

export const getBusinessOwnerEmailCall = async (gymName: string) => {
  try {
    const email = await fetchBusinessOwnerEmailByName(gymName);
    return email;
  } catch (error) {
    // console.error("Failed to fetch business owner email:", error);
    return null;
  }
};


// Updated API operations to match backend expectations

export interface OnboardingPayload {
  isOnboarded: boolean;
  checkInCode?: string;
}

export interface ConversionPayload {
  isConverted: boolean;
}

export const updateMembershipOnboardingCall = async (
  membershipId: string, 
  isOnboarded: boolean,
  checkInCode?: string
) => {
  try {
    const payload: OnboardingPayload = { 
      isOnboarded,
      ...(checkInCode && { checkInCode })
    };
    
    const result = await updateMembershipOnboarding(membershipId, payload);
    return result?.data;
  } catch (error: unknown) {
    
    if (error && typeof error === 'object' && 'response' in error) {
      const errorWithResponse = error as { response: { status: number; data: unknown } };
    }
    
    throw error;
  }
};

export const updateMembershipConversionCall = async (membershipId: string, isConverted: boolean) => {
  try {
    // Just send isConverted - the backend generates the timestamp
    const payload: ConversionPayload = { 
      isConverted 
    };
    
    // console.log("Sending conversion payload:", payload);
    
    const result = await updateMembershipConversion(membershipId, payload);
    return result?.data;
  } catch (error: unknown) {
    // console.error("updateMembershipConversionCall error:", error);
    
    if (error && typeof error === 'object' && 'response' in error) {
      const errorWithResponse = error as { response: { status: number; data: unknown } };
      // console.error("Error response:", {
      //   status: errorWithResponse.response.status,
      //   data: errorWithResponse.response.data
      // });
    }
    return null;
  }
};

export const getSignedGalleryUrlCall = async (
  imageData1: any,
  imageData2: any,
  imageData3: any
) => {
  try {
    const fileData1 = imageData1[0]?.file as any;
    const formData = new FormData();
    formData.append("image", fileData1);

    const fileData2 = imageData2[0]?.file as any;
    const formData2 = new FormData();
    formData2.append("image", fileData2);

    const fileData3 = imageData3[0]?.file as any;
    const formData3 = new FormData();
    formData3.append("image", fileData3);

    const uploadImageResult: any = await Promise.all([
      uploadImage(formData),
      uploadImage(formData2),
      uploadImage(formData3),
    ]);

    if (
      (uploadImageResult?.[0].status === 200 ||
        uploadImageResult?.[0].status === 201) &&
      (uploadImageResult?.[1].status === 200 ||
        uploadImageResult?.[1].status === 201) &&
      (uploadImageResult?.[2].status === 200 ||
        uploadImageResult?.[2].status === 201)
    ) {
      const updateGalleryResult = await updateGallery([
        {
          resourceUri: uploadImageResult?.[0]?.data?.resourceUri,
          contentType: imageData1?.[0]?.file?.type, // mimeTypes
        },
        {
          resourceUri: uploadImageResult?.[1]?.data?.resourceUri,
          contentType: imageData2?.[0]?.file?.type, // mimeTypes
        },
        {
          resourceUri: uploadImageResult?.[2]?.data?.resourceUri,
          contentType: imageData3?.[0]?.file?.type, // mimeTypes
        },
      ]);

      if (
        updateGalleryResult?.status === 200 ||
        updateGalleryResult?.status === 201
      )
        return "success";
    }
  } catch (error) {
    alert(`Gallery update status:, ${error}`);
    return "error";
  }
};

// DISCOUNT CODE FUNCTIONALITY

// Function to validate a discount code
export const validateDiscountCodeCall = async (code: string, subscriptionId?: string, businessProductId?: string) => {
  try {
    const result = await validateDiscountCode(code, subscriptionId, businessProductId);

    if (result?.data?.valid) {
      return {
        valid: true,
        code: result.data.discount.code || "",
        type: result.data.discount.type || "",
        amount: result.data.discount.amount || 0,
        description: result.data.discount.description || "",
        stripePromoId: result.data.discount.stripePromoId || null,
      };
    } else {
      return {
        valid: false,
        message: result?.data?.message || "Invalid discount code",
        code: "",
        type: "",
        amount: 0,
        description: "",
        stripePromoId: null,
      };
    }
  } catch (error: any) {
    const message = error?.response?.data?.message || "Invalid discount code";
    return {
      valid: false,
      message,
      code: "",
      type: "",
      amount: 0,
      description: "",
      stripePromoId: null,
    };
  }
};

export const recordDiscountCodeUsageCall = async (
  code: string,
  subscriptionId: string,
  email: string
) => {
  try {
    let retries = 3;
    let success = false;
    let error = null;

    while (retries > 0 && !success) {
      try {
        const result = await recordDiscountCodeUsage({ code, subscriptionId, email });

        if (result?.data?.success) {
          // console.log("Successfully recorded discount code usage");
          success = true;
          return { success: true };
        } else {
          // console.warn("Failed to record discount code usage: API returned success: false");
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      } catch (err) {
        error = err;
        // console.error(`Attempt ${4 - retries}/3: Failed to record discount code usage:`, err);
        await new Promise(resolve => setTimeout(resolve, 1000));
      }

      retries--;
    }

    if (!success) {
      // console.error("All attempts to record discount code usage failed:", error);
    }

    return { success: false };
  } catch (error) {
    // console.error("recordDiscountCodeUsageCall error:", error);
    return { success: false };
  }
};

// ------- Referral Codes -------

export const getUserReferralCodeCall = async () => {
  try {
    const result = await getUserReferralCode();
    return result?.data;
  } catch (error) {
    // console.error("getUserReferralCodeCall error:", error);
    return {
      hasReferralCode: false,
      message: "Failed to retrieve referral code"
    };
  }
};

// ------- Waitlist -------

export const submitTampaWaitlistEntryCall = async (entry: {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  favoriteGym?: string;
}) => {
  try {
    const result = await submitTampaWaitlistEntry(entry);
    return result;
  } catch (error) {
    // console.error("submitWaitlistEntryCall error:", error);
    throw error;
  }
};

export const submitOrlandoWaitlistEntryCall = async (entry: {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  favoriteGym?: string;
}) => {
  try {
    const result = await submitOrlandoWaitlistEntry(entry);
    return result;
  } catch (error) {
    // console.error("submitWaitlistEntryCall error:", error);
    throw error;
  }
};


export const submitNewYorkWaitlistEntryCall = async (entry: {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  favoriteGym?: string;
}) => {
  try {
    const result = await submitNewYorkWaitlistEntry(entry);
    return result;
  } catch (error) {
    console.error("submitWaitlistEntryCall error:", error);
    throw error;
  }
};

export const submitPartnerLeadCall = async (entry: {
  name: string;
  businessName: string;
  email: string;
  phone: string;
  zipCode: string;
  locationCount: string;
  website?: string;
  source: string;
}) => {
  try {
    const result = await submitPartnerLead(entry);
    return result;
  } catch (error) {
    console.error("submitPartnerLeadCall error:", error);
    throw error;
  }
};

export const trackPurchaseConversionCall = async (opts: {
  email?: string;
  phone?: string;
  value: number;
  currency?: string;
  eventId?: string;
}) => {
  try {
    await api.post("/v1/conversions/purchase", {
      email: opts.email,
      phone: opts.phone,
      value: opts.value,
      currency: opts.currency || "USD",
      eventId: opts.eventId,
    });
  } catch (error) {
    // Fire-and-forget: log but never throw
    console.error("trackPurchaseConversionCall error:", error);
  }
};